import { Link } from "react-router-dom";
import { Minus, Plus, ArrowRight } from "lucide-react";
import Seo from "@/shop/Seo";
import { useCart } from "@/shop/CartContext";
import { fmt, productPath } from "@/shop/api";

export default function CartPage() {
  const { items, setQty, removeItem, subtotal, config, count } = useCart();
  const threshold = config.free_shipping_threshold || 0;
  const progress = threshold > 0 ? Math.min(100, (subtotal / threshold) * 100) : 0;
  const missing = Math.max(0, threshold - subtotal);

  return (
    <div className="bg-ink text-bone min-h-screen" data-testid="cart-page">
      <Seo
        title="Carrello — Stillwater"
        description="Il tuo carrello Stillwater."
      />
      <header className="px-5 md:px-10 pt-24 md:pt-28 pb-8 border-b border-ink-border">
        <h1 className="font-serif font-light text-3xl md:text-5xl tracking-tight">
          Il tuo <span className="italic text-ember">carrello</span>
        </h1>
      </header>

      {items.length === 0 ? (
        <div className="px-5 md:px-10 py-20 text-center" data-testid="cart-empty">
          <p className="font-serif italic text-2xl">Il carrello è vuoto, come una riva all'alba.</p>
          <Link
            to="/shop"
            data-testid="cart-empty-cta"
            className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.25em] text-ember hover:underline"
          >
            Visita lo Shop
          </Link>
        </div>
      ) : (
        <div className="px-5 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-12">
          <div>
            {items.map((i) => (
              <div
                key={i.sku}
                data-testid={`cart-item-${i.sku}`}
                className="flex gap-5 py-5 border-b border-ink-border"
              >
                <Link to={productPath({ sku: i.sku, slug: i.slug, product_type: i.type, brand_slug: "" })} className="shrink-0 w-20 md:w-24 aspect-square overflow-hidden border border-ink-border">
                  <img src={i.image || "/photos/tre-canne.jpg"} alt={i.name} className="h-full w-full object-cover" />
                </Link>
                <div className="flex-1">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-faint">{i.brand}</p>
                  <p className="font-serif text-base md:text-lg mt-1">{i.name}</p>
                  <div className="mt-3 flex items-center gap-4">
                    <div className="flex items-center border border-ink-border rounded-full">
                      <button data-testid={`qty-minus-${i.sku}`} onClick={() => setQty(i.sku, i.qty - 1)} className="px-3 py-1.5 text-mist hover:text-bone" aria-label="Riduci">
                        <Minus size={12} strokeWidth={1.5} />
                      </button>
                      <span className="w-7 text-center font-mono text-xs">{i.qty}</span>
                      <button
                        data-testid={`qty-plus-${i.sku}`}
                        onClick={() => setQty(i.sku, i.qty + 1)}
                        disabled={i.track && i.qty >= i.maxQty}
                        className="px-3 py-1.5 text-mist hover:text-bone disabled:opacity-30"
                        aria-label="Aumenta"
                      >
                        <Plus size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                    <button
                      data-testid={`remove-${i.sku}`}
                      onClick={() => removeItem(i.sku)}
                      className="font-mono text-[9px] uppercase tracking-[0.2em] text-faint hover:text-ember transition-colors"
                    >
                      Rimuovi
                    </button>
                  </div>
                </div>
                <p className="font-mono text-sm">{fmt(i.price * i.qty)}</p>
              </div>
            ))}
          </div>

          <aside className="h-fit lg:sticky lg:top-28 border border-ink-border bg-ink-surface p-6 md:p-8">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Riepilogo</h2>
            <div className="mt-5 space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-mist">Oggetti</span>
                <span className="font-mono">{count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mist">Subtotale</span>
                <span className="font-mono" data-testid="cart-subtotal">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mist">Spedizione</span>
                <span className="font-mono text-xs text-mist" data-testid="cart-shipping">calcolata dopo la conferma</span>
              </div>
            </div>

            {threshold > 0 && (
              <div className="mt-6" data-testid="free-shipping-progress">
                <div className="h-1 bg-ink-border rounded-full overflow-hidden">
                  <div className="h-full bg-ember transition-all duration-700" style={{ width: `${progress}%` }} />
                </div>
                <p className="mt-2.5 font-mono text-[9px] uppercase tracking-[0.2em] text-faint">
                  {missing > 0
                    ? `Ti mancano ${fmt(missing)} per la spedizione gratuita`
                    : "Spedizione gratuita raggiunta"}
                </p>
              </div>
            )}

            <Link
              to="/checkout"
              data-testid="checkout-cta"
              className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-6 py-4 rounded-full hover:bg-ember-bright transition-colors duration-300"
            >
              Vai al checkout
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-faint text-center">
              IVA inclusa — pagamenti sicuri via Stripe
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
